<template>
  <wrapper>
    <div class="flex flex-col" @keyup.enter="onSearch">
      <div class="flex items-center gap-4">
        <div class="relative flex flex-2">
          <input
            type="text"
            placeholder="Search for article name/externalidentifer or orderreference"
            class="w-full p-4 pl-10 text-white bg-transparent border border-white border-solid rounded"
            :value="searchText"
            @input="$emit('input', $event.target.value)"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute inset-0 w-5 h-5 search-position text-gray"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="relative flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute inset-0 w-5 h-5 search-position text-gray"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
              clip-rule="evenodd"
            />
          </svg>
          <datepicker
            :value="startDate"
            :monday-first="true"
            @selected="$emit('startDate', $event)"
            calendar-class="text-gray"
            input-class="p-4 pl-10 text-white bg-transparent border border-white border-solid rounded"
            placeholder="From"
          ></datepicker>
        </div>
        <div class="relative flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute inset-0 w-5 h-5 search-position text-gray"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
              clip-rule="evenodd"
            />
          </svg>
          <datepicker
            :value="endDate"
            :monday-first="true"
            @selected="$emit('endDate', $event)"
            calendar-class="text-gray"
            input-class="p-4 pl-10 text-white bg-transparent border border-white border-solid rounded"
            placeholder="To"
          ></datepicker>
        </div>
      </div>

      <div class="flex items-center gap-4 mt-4">
        <div class="relative flex flex-1">
          <input
            type="number"
            placeholder="Serial Number"
            class="w-full p-4 pl-10 text-white bg-transparent border border-white border-solid rounded"
            :value="serialNumber"
            @input="handleSerialNumberInput($event)"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="absolute inset-0 w-5 h-5 search-position text-gray"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="flex-1">
          <drop-down
            placeholder="Filter by counter (optional)"
            value-property="id"
            search-prop="name"
            :options="counters"
            :value="selectedCounter"
            @input="handleCounterInput($event)"
          />
        </div>
      </div>

      <div class="flex flex-col mt-6">
        <div class="flex items-center">
          <h3>Variables</h3>
          <div class="flex ml-auto">
            <drop-down
              placeholder="Variable Keys"
              value-property="key"
              searchProp="key"
              :options="variableKeys"
              v-model="selectedVariable"
            />
            <button
              :disabled="!selectedVariable"
              @click="handleAdd"
              type="button"
              class="px-4 py-2 ml-auto bg-transparent border border-solid rounded cursor-pointer border-highlight text-highlight hover:bg-highlight hover:text-white"
            >
              Add
            </button>
          </div>
        </div>
        <div
          class="grid grid-cols-5 gap-4 p-4 mt-4 rounded-lg bg-primary-light"
          v-for="(variable, index) in variables"
          :key="index"
        >
          <grafokett-input
            class="col-span-1"
            v-model="variable.key"
            :editable="false"
          />
          <div class="flex col-span-4 gap-4">
            <grafokett-input
              v-model="variable.data"
              labelPlaceholder="Data"
              :editable="true"
              :is-expandable="true"
            />
            <svg
              @click="removeVariable(index)"
              xmlns="http://www.w3.org/2000/svg"
              class="self-center w-5 h-5 cursor-pointer text-highlight"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
        </div>
      </div>
      <div class="flex items-center mt-6 mr-auto">
        <button
          type="button"
          class="mr-4 button primary white"
          @click="onSearch"
        >
          Search
        </button>
        <button
          @click="clearForm"
          class="flex items-center font-bold bg-transparent border-none cursor-pointer text-highlight"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Clear
        </button>
      </div>
    </div>
  </wrapper>
</template>

<script>
import GrafokettInput from "../forms/Input.vue";
import DropDown from "../forms/DropDown.vue";
import Datepicker from "vuejs-datepicker";

export default {
  components: {
    GrafokettInput,
    DropDown,
    Datepicker,
  },
  props: {
    variableKeys: { type: Array },
    variables: { type: Array },
    removeVariable: { type: Function },
    onSearch: { type: Function },
    addVariable: { type: Function },
    clearForm: { type: Function },
    searchText: { type: String },
    startDate: { type: Date },
    endDate: { type: Date },
    serialNumber: { type: Number },
    counters: { type: Array, default: () => [] },
    counterId: { type: Number, default: null },
  },
  data() {
    return {
      selectedVariable: null,
      selectedCounter: null,
    };
  },
  methods: {
    handleAdd() {
      this.addVariable(this.selectedVariable);
      this.selectedVariable = null;
    },
    handleSerialNumberInput(event) {
      const value = event.target.value;
      if (value === "" || value === null || value === undefined) {
        this.$emit("serialNumberInput", null);
      } else {
        const parsed = parseInt(value, 10);
        this.$emit("serialNumberInput", isNaN(parsed) ? null : parsed);
      }
    },
    handleCounterInput(counter) {
      this.selectedCounter = counter || null;
      this.$emit("counterInput", counter ? counter.id : null);
    },
  },
  watch: {
    counterId(val) {
      if (val === null) {
        this.selectedCounter = null;
      }
    },
  },
};
</script>

<style scoped>
.search-position {
  top: 25%;
  left: 16px;
}

input[type="text"]::placeholder {
  @apply text-gray;
}

.vd-wrapper {
  flex: 1 1 0% !important;
}
</style>
